/**
 * Action Permission Hook
 * 
 * Exposes action-level permission guards for the current role.
 * Components use this to hide/disable/tooltip UI controls.
 */
import { useMemo } from "react";
import { useRole } from "@/data/RoleContext";
import {
  canPerformAction,
  getActionRestriction,
  isSensitiveAction,
  canOverride,
  type ActionKey,
} from "@/data/actionPermissionMatrix";

export function useActionPermissions() {
  const { role } = useRole();

  return useMemo(() => ({
    role,

    /** Can the current role perform this action? */
    can: (action: ActionKey) => canPerformAction(role, action),

    /** Get restriction tooltip text, or null if allowed */
    restriction: (action: ActionKey) => getActionRestriction(role, action),

    /** Is this action sensitive (requires audit)? */
    isSensitive: (action: ActionKey) => isSensitiveAction(action),

    /** Can the current role override this action? */
    canOverride: (action: ActionKey) => canOverride(role, action),

    /** Helper: returns props for a button that should be disabled with tooltip */
    guardProps: (action: ActionKey) => {
      const allowed = canPerformAction(role, action);
      const reason = getActionRestriction(role, action);
      return {
        disabled: !allowed,
        title: reason ?? undefined,
        "aria-disabled": !allowed,
      };
    },
  }), [role]);
}
